import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Smartphone, Code2, Globe, Share2, TrendingUp, Rocket, Brain, CreditCard, BarChart3, Search, UserCheck, Timer, Zap, CalendarClock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import LeadFormModal from "@/components/LeadFormModal";
import { TEST_IDS } from "@/constants/testIds";

function formatINR(amount) {
  return `₹${amount.toLocaleString("en-IN")}`;
}

const serviceOptions = [
  { id: "mobile-app-dev", label: "Mobile App Development", icon: Smartphone, baseMin: 1000000, baseMax: 1500000 },
  { id: "custom-software-dev", label: "Custom Software Engineering", icon: Code2, baseMin: 800000, baseMax: 1200000 },
  { id: "web-dev", label: "Web Development", icon: Globe, baseMin: 400000, baseMax: 700000 },
  { id: "social-media-marketing", label: "Social Media Marketing", icon: Share2, baseMin: 200000, baseMax: 400000 },
  { id: "business-development", label: "Business Development", icon: TrendingUp, baseMin: 250000, baseMax: 500000 },
  { id: "product-marketing", label: "Product Marketing", icon: Rocket, baseMin: 300000, baseMax: 600000 },
];

const scaleOptions = [
  { id: "mvp", label: "Startup MVP", desc: "Lean prototype, core features only", multiplier: 1.0 },
  { id: "growth", label: "Growth Business", desc: "Production-grade, scalable platform", multiplier: 2.0 },
  { id: "enterprise", label: "Enterprise Grade", desc: "Full-scale architecture with SLA", multiplier: 3.5 },
];

const addonOptions = [
  { id: "ai_ml", label: "AI / ML Models", icon: Brain, cost: 500000 },
  { id: "payment", label: "Payment Gateway", icon: CreditCard, cost: 150000 },
  { id: "analytics", label: "Real-time Analytics", icon: BarChart3, cost: 300000 },
  { id: "seo", label: "SEO / Growth Package", icon: Search, cost: 200000 },
  { id: "pm", label: "Dedicated Project Manager", icon: UserCheck, cost: 150000 },
];

const timelineOptions = [
  { id: "accelerated", label: "Accelerated Sprint", desc: "Fastest delivery (+30%)", icon: Zap, modifier: 1.3 },
  { id: "standard", label: "Standard Launch", desc: "Balanced pace", icon: Timer, modifier: 1.0 },
  { id: "phased", label: "Phased Rollout", desc: "Incremental delivery (-10%)", icon: CalendarClock, modifier: 0.9 },
];

export default function QuoteCalculator() {
  const [selectedServices, setSelectedServices] = useState([]);
  const [scale, setScale] = useState("");
  const [addons, setAddons] = useState([]);
  const [timeline, setTimeline] = useState("");
  const [showForm, setShowForm] = useState(false);

  const toggleService = (id) => {
    setSelectedServices((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };
  const toggleAddon = (id) => {
    setAddons((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);
  };

  const estimate = useMemo(() => {
    if (selectedServices.length === 0) return { min: 0, max: 0 };
    const scaleMult = scaleOptions.find((s) => s.id === scale)?.multiplier || 1;
    const timeMod = timelineOptions.find((t) => t.id === timeline)?.modifier || 1;

    let min = 0, max = 0;
    selectedServices.forEach((sid) => {
      const svc = serviceOptions.find((s) => s.id === sid);
      if (svc) { min += svc.baseMin; max += svc.baseMax; }
    });
    min *= scaleMult; max *= scaleMult;

    const addonTotal = addons.reduce((sum, aid) => {
      const addon = addonOptions.find((a) => a.id === aid);
      return sum + (addon?.cost || 0);
    }, 0);
    min += addonTotal; max += addonTotal;

    min *= timeMod; max *= timeMod;
    return { min: Math.round(min), max: Math.round(max) };
  }, [selectedServices, scale, addons, timeline]);

  const prefillData = {
    services_selected: selectedServices,
    project_scale: scale,
    addons,
    timeline,
    estimated_min: estimate.min,
    estimated_max: estimate.max,
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Badge variant="outline" className="mb-4 border-[#00F2FE]/30 text-[#00F2FE] bg-[#00F2FE]/5 px-3 py-1 text-xs tracking-wider uppercase">
            Smart Calculator
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-3">
            Project <span className="gradient-text">Cost Estimator</span>
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto text-sm sm:text-base">
            Configure your project scope and get an instant budget estimate in INR.
          </p>
        </motion.div>

        <div data-testid={TEST_IDS.calculator.section} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Selections */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Services */}
            <motion.div className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <h3 className="font-heading font-semibold text-lg text-slate-100 mb-1">Step 1: Select Services</h3>
              <p className="text-slate-500 text-sm mb-5">Choose one or more service pillars</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {serviceOptions.map((svc) => {
                  const active = selectedServices.includes(svc.id);
                  return (
                    <button
                      key={svc.id}
                      data-testid={TEST_IDS.calculator.serviceOption(svc.id)}
                      onClick={() => toggleService(svc.id)}
                      className={`flex items-center gap-3 p-4 rounded-lg border text-left transition-all ${
                        active ? "border-[#00F2FE]/40 bg-[#00F2FE]/5" : "border-white/[0.06] bg-[#0F172A] hover:border-white/[0.12]"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        active ? "bg-[#00F2FE]/20" : "bg-white/[0.04]"
                      }`}>
                        <svc.icon className={`h-5 w-5 ${active ? "text-[#00F2FE]" : "text-slate-500"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium block ${active ? "text-slate-100" : "text-slate-400"}`}>{svc.label}</span>
                        <span className="text-xs text-slate-600 font-mono">{formatINR(svc.baseMin)} - {formatINR(svc.baseMax)}</span>
                      </div>
                      {active && <CheckCircle2 className="h-5 w-5 text-[#00F2FE] shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {/* Step 2: Scale */}
            <motion.div className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <h3 className="font-heading font-semibold text-lg text-slate-100 mb-1">Step 2: Project Scale</h3>
              <p className="text-slate-500 text-sm mb-5">Choose the complexity level</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {scaleOptions.map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={TEST_IDS.calculator.scaleOption(opt.id)}
                    onClick={() => setScale(opt.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      scale === opt.id ? "border-[#0EA5E9]/40 bg-[#0EA5E9]/5" : "border-white/[0.06] hover:border-white/[0.12]"
                    }`}
                  >
                    <span className={`text-sm font-semibold block mb-1 ${scale === opt.id ? "text-[#0EA5E9]" : "text-slate-300"}`}>{opt.label}</span>
                    <span className="text-xs text-slate-500">{opt.desc}</span>
                    <span className="text-xs font-mono text-slate-600 block mt-1">{opt.multiplier}x multiplier</span>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Step 3: Addons */}
            <motion.div className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <h3 className="font-heading font-semibold text-lg text-slate-100 mb-1">Step 3: Feature Add-ons</h3>
              <p className="text-slate-500 text-sm mb-5">Enhance your project with premium capabilities</p>
              <div className="space-y-3">
                {addonOptions.map((addon) => (
                  <label
                    key={addon.id}
                    data-testid={TEST_IDS.calculator.addonOption(addon.id)}
                    className={`flex items-center gap-4 p-3.5 rounded-lg border cursor-pointer transition-all ${
                      addons.includes(addon.id) ? "border-[#3B82F6]/40 bg-[#3B82F6]/5" : "border-white/[0.06] hover:border-white/[0.12]"
                    }`}
                  >
                    <Checkbox checked={addons.includes(addon.id)} onCheckedChange={() => toggleAddon(addon.id)} className="border-white/20 data-[state=checked]:bg-[#3B82F6] data-[state=checked]:border-[#3B82F6]" />
                    <addon.icon className={`h-5 w-5 ${addons.includes(addon.id) ? "text-[#3B82F6]" : "text-slate-500"}`} />
                    <span className="flex-1 text-sm text-slate-300">{addon.label}</span>
                    <span className="text-xs font-mono text-slate-500">{formatINR(addon.cost)}</span>
                  </label>
                ))}
              </div>
            </motion.div>

            {/* Step 4: Timeline */}
            <motion.div className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <h3 className="font-heading font-semibold text-lg text-slate-100 mb-1">Step 4: Delivery Timeline</h3>
              <p className="text-slate-500 text-sm mb-5">Select your preferred delivery pace</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {timelineOptions.map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={TEST_IDS.calculator.timelineOption(opt.id)}
                    onClick={() => setTimeline(opt.id)}
                    className={`flex items-center gap-3 p-4 rounded-lg border text-left transition-all ${
                      timeline === opt.id ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/[0.06] hover:border-white/[0.12]"
                    }`}
                  >
                    <opt.icon className={`h-5 w-5 ${timeline === opt.id ? "text-emerald-400" : "text-slate-500"}`} />
                    <div>
                      <span className={`text-sm font-medium block ${timeline === opt.id ? "text-emerald-400" : "text-slate-300"}`}>{opt.label}</span>
                      <span className="text-xs text-slate-500">{opt.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right: Live Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <motion.div
                data-testid={TEST_IDS.calculator.totalDisplay}
                className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6 space-y-5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-100 mb-1">Estimated Budget</h3>
                  <p className="text-slate-500 text-xs">Real-time calculation in INR</p>
                </div>

                <div className="bg-[#060913] rounded-lg p-5 border border-white/[0.04]">
                  {estimate.min > 0 ? (
                    <>
                      <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Estimated Range</p>
                      <p className="font-mono text-2xl sm:text-3xl font-bold gradient-text leading-tight">{formatINR(estimate.min)}</p>
                      <p className="text-slate-500 text-xs my-1">to</p>
                      <p className="font-mono text-2xl sm:text-3xl font-bold text-slate-200 leading-tight">{formatINR(estimate.max)}</p>
                    </>
                  ) : (
                    <p className="text-slate-500 text-sm">Select services to see estimate</p>
                  )}
                </div>

                <Separator className="bg-white/[0.06]" />

                {/* Breakdown */}
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Services ({selectedServices.length})</span>
                    <span className="font-mono text-slate-300">{selectedServices.length > 0 ? `${formatINR(selectedServices.reduce((s, sid) => s + (serviceOptions.find(o => o.id === sid)?.baseMin || 0), 0))}` : "--"}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Scale</span>
                    <span className="font-mono text-slate-300">{scale ? `${scaleOptions.find(s => s.id === scale)?.multiplier}x` : "--"}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Add-ons ({addons.length})</span>
                    <span className="font-mono text-slate-300">{addons.length > 0 ? formatINR(addons.reduce((s, aid) => s + (addonOptions.find(a => a.id === aid)?.cost || 0), 0)) : "--"}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Timeline</span>
                    <span className="font-mono text-slate-300">{timeline ? timelineOptions.find(t => t.id === timeline)?.label : "--"}</span>
                  </div>
                </div>

                <Button
                  data-testid={TEST_IDS.calculator.submitBtn}
                  onClick={() => setShowForm(true)}
                  disabled={selectedServices.length === 0}
                  className="w-full bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-[#060913] font-bold hover:opacity-90 py-5 text-base gap-2"
                >
                  Submit Inquiry <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="text-slate-600 text-[11px] text-center">No commitment. Free consultation included.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <LeadFormModal open={showForm} onOpenChange={setShowForm} prefillData={prefillData} />
    </div>
  );
}
